import { computed, DestroyRef, inject, Signal } from '@angular/core';
import { signalStoreFeature, withState, withMethods, withComputed, patchState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { interval, Subject, switchMap, takeUntil, tap, startWith, filter, EMPTY } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface ResourcePollingState {
  isPolling: boolean;
  pollingInterval: number;
  lastPolledAt: number | null;
  pollingError: string | null;
  pollCount: number;
  retryCount: number;
}

export interface ResourcePollingConfig {
  intervalMs?: number;
  shouldPollWhen?: () => boolean;
  onPollError?: (error: any) => void;
  maxPolls?: number;
  maxRetries?: number;
  pollingName?: string;
}

export function withResourcePolling(config: ResourcePollingConfig = {}) {
  const { intervalMs = 10000, shouldPollWhen, onPollError, maxPolls, maxRetries = 5, pollingName = '' } = config;

  return signalStoreFeature(
    withState<ResourcePollingState>({
      isPolling: false,
      pollingInterval: intervalMs,
      lastPolledAt: null,
      pollingError: null,
      pollCount: 0,
      retryCount: 0,
    }),
    withMethods((store, destroyRef = inject(DestroyRef)) => {
      const stopPolling$ = new Subject<void>();
      return {
        stopResourcePolling: () => {
          if (store.isPolling()) {
            stopPolling$.next();
            patchState(store, { isPolling: false });
            console.log('Resource polling stopped');
          }
        },
        startResourcePolling: rxMethod<{
          resources: Array<{ reload: { reload: () => void }; condition: Signal<boolean> }>;
          stopCondition?: () => boolean;
          intervalMs?: number;
        }>(
          switchMap(({ resources, intervalMs: customInterval, stopCondition }) => {
            const pollingInterval = customInterval || store.pollingInterval();
            // Stops the polling if condition is provided
            // Else stop is manually via stopResourcePolling
            if (typeof stopCondition === 'function' && stopCondition() && store.isPolling()) {
              stopPolling$.next();
              patchState(store, { isPolling: false });
              console.log('Resource polling stopped');
              return EMPTY;
            }

            // Update state to indicate polling started
            patchState(store, {
              isPolling: true,
              pollingError: null,
              pollCount: 0,
              pollingInterval: pollingInterval,
            });

            return interval(pollingInterval).pipe(
              startWith(0), // Immediate first poll
              takeUntil(stopPolling$),
              takeUntilDestroyed(destroyRef),
              // Apply global condition check
              filter(() => {
                return shouldPollWhen ? shouldPollWhen() : true;
              }),
              tap(() => {
                try {
                  const currentCount = store.pollCount();
                  // Update state
                  patchState(store, {
                    lastPolledAt: Date.now(),
                    pollCount: currentCount + 1,
                  });

                  // Stop if max polls reached
                  if (maxPolls && store.pollCount() >= maxPolls) {
                    stopPolling$.next();
                    patchState(store, { isPolling: false });
                    console.log(`[RESOURCE POLLING]: ${pollingName} stopped.`);
                    return;
                  }

                  // Stop if max reties have been reached
                  if (store.retryCount() >= maxRetries) {
                    stopPolling$.next();
                    patchState(store, { isPolling: false });
                    console.log(`[RESOURCE POLLING]: ${pollingName} stopped.`);
                    return;
                  }

                  // Reload all provided resources
                  resources.forEach(({ reload, condition }) => {
                    const willPoll = condition();
                    if (typeof reload.reload === 'function' && willPoll) {
                      reload.reload();
                    }
                  });
                } catch (error) {
                  console.log(`[RESOURCE POLLING]: ERROR ${pollingName} ${error}`);
                  const retryCount = store.retryCount();
                  patchState(store, {
                    pollingError: error instanceof Error ? error.message : 'Unknown polling error',
                    retryCount: retryCount + 1,
                  });

                  if (onPollError) {
                    onPollError(error);
                  }
                }
              }),
            );
          }),
        ),
      };
    }),

    withMethods((store, destroyRef = inject(DestroyRef)) => {
      return {
        resetPollingError: () => {
          patchState(store, { pollingError: null, retryCount: 0 });
        },

        setPollingInterval: (intervalMs: number) => {
          patchState(store, { pollingInterval: intervalMs });

          // Restart polling with new interval if currently active
          if (store.isPolling()) {
            store.stopResourcePolling();
            // Note: Would need to store the resources and condition to restart.
            // Something to figure out.
            // Might need to manually restart
            console.log(`[RESOURCE POLLING]: Updated ${pollingName} interval.`);
          }
        },
      };
    }),

    withComputed(({ isPolling, pollingInterval, lastPolledAt, pollingError, pollCount }) => ({
      pollingStatus: computed(() => {
        const polledAt = lastPolledAt();
        return {
          isActive: isPolling(),
          interval: pollingInterval(),
          lastPolled: polledAt ? new Date(polledAt) : null,
          error: pollingError(),
          count: pollCount(),
        };
      }),

      timeSinceLastPoll: computed(() => {
        const lastPoll = lastPolledAt();
        return lastPoll ? Date.now() - lastPoll : null;
      }),

      hasPollingError: computed(() => !!pollingError()),
    })),
  );
}
